import {Injectable} from '@angular/core';
import {Subject, Subscription} from 'rxjs';
import {Store} from '@ngrx/store';
import {AppState} from '@app/core/models/app-state.interface';
import {msalGuardConfig} from '@app/features/auth/auth.config';
import {MsalBroadcastService, MsalService} from '@azure/msal-angular';
import {filter, takeUntil} from 'rxjs/operators';
import {AuthenticationResult, EventMessage, EventType, InteractionRequiredAuthError} from '@azure/msal-browser';
import {GetInspector} from '@app/features/auth/redux/auth.actions';
import {getInspector} from '@app/features/auth/redux/auth.selectors';
import {InspectorInterface, InspectorTypeEnum} from '@app/core/models/inspector.interface';
import {Router} from '@angular/router';
import {jwtDecode} from 'jwt-decode';
import {environment} from '../../../../environments/environment';
import {ErrorManagerService} from '@app/core/services/error-manager.service';


const ACCESS_TOKEN_LOCAL_STORAGE_KEY = 'auth.token';
const INTERNAL_LOCAL_STORAGE_KEY = 'auth.internal';

// Massimo numero di errori consecutivi prima di aprire il circuit breaker
const CB_FAILURE_THRESHOLD = 5;
// Tempo di blocco del circuit breaker in ms (5 minuti)
const CB_TIMEOUT_MS = 300_000;
// Numero massimo di retry prima di aprire il circuit breaker
const MAX_RETRIES = 3;
// Delay base per il backoff esponenziale in ms
const BASE_DELAY_MS = 2_000;


@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private name: string;
  private token: string;
  private internal: boolean;
  private readonly _destroying$ = new Subject<void>();
  private subscription$ = new Subscription();

  inspector: InspectorInterface;

  // --- Stato interno autenticazione ---
  private authState = {
    isInitializing: false,
    isLoggingIn: false,
    lastLoginAttempt: 0,
    failureCount: 0,
  };

  private circuitBreaker = {
    isOpen: false,
    openedAt: 0,
  };


  constructor(
    private store: Store<AppState>,
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private router: Router,
    private errorManager: ErrorManagerService,
  ) {

    this.token = localStorage.getItem(ACCESS_TOKEN_LOCAL_STORAGE_KEY);
    this.internal = JSON.parse(localStorage.getItem(INTERNAL_LOCAL_STORAGE_KEY));

    this.subscription$.add(
      this.store.select(getInspector).pipe(
        filter((inspector) => !!inspector)
      ).subscribe(inspector => {
        this.inspector = inspector;
        this.internal = inspector.type === InspectorTypeEnum.INTERNO;
        this.name = jwtDecode(this.token.split(' ')[1])['unique_name'];
        this.authState.failureCount = 0;
        this.circuitBreaker.isOpen = false;
        const currentPath = window.location.pathname.replace(/\/+$/, '') || '/';
        if (currentPath === '/' || currentPath === '/login') {
          console.log('[AUTH] Inspector caricato, navigazione a /');
          this.router.navigateByUrl('/');
        } else {
          console.log('[AUTH] Inspector caricato, mantengo URL:', currentPath);
        }
      })
    );

    // Sottoscrizione unica agli eventi MSAL — evita subscription duplicate su ogni checkLogin()
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) =>
          msg.eventType === EventType.LOGIN_SUCCESS ||
          msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
          msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE
        ),
        takeUntil(this._destroying$)
      )
      .subscribe((result: EventMessage) => {
        this.authState.isInitializing = false;
        this.authState.isLoggingIn = false;

        if (result.error) {
          this.handleMsalError(result);
        } else {
          this.handleMsalSuccess(result);
        }
      });
  }


  // ---------------------------------------------------------------------------
  // Login
  // ---------------------------------------------------------------------------

  async checkLogin(): Promise<void> {
    if (this.authState.isInitializing || this.authState.isLoggingIn) {
      console.warn('[AUTH] Login già in corso, salto duplicato');
      return;
    }

    if (this.isCircuitBreakerOpen()) {
      return;
    }

    this.authState.isInitializing = true;
    this.authState.lastLoginAttempt = Date.now();
    console.log('[AUTH] Avvio processo di autenticazione');

    const configs = msalGuardConfig.authRequest as any;
    const accounts = this.msalService.instance.getAllAccounts();

    if (accounts.length > 0) {
      this.msalService.instance.setActiveAccount(accounts[0]);
      configs.account = accounts[0];
      this.msalService.acquireTokenSilent(configs).subscribe({
        error: (err) => {
          console.warn('[AUTH] acquireTokenSilent fallito, fallback a loginRedirect:', err.message);
          this.authState.isInitializing = false;
          this.msalService.loginRedirect(configs).subscribe({
            error: (redirectErr) => {
              console.error('[AUTH] Errore loginRedirect dopo fallback:', redirectErr);
            }
          });
        }
      });
    } else {
      this.msalService.loginRedirect(configs).subscribe({
        complete: () => { this.authState.isInitializing = false; },
        error: (err) => {
          console.error('[AUTH] Errore loginRedirect:', err);
          this.authState.isInitializing = false;
        }
      });
    }
  }


  // ---------------------------------------------------------------------------
  // Gestione successo MSAL
  // ---------------------------------------------------------------------------

  private handleMsalSuccess(result: EventMessage): void {
    const payload = result.payload as AuthenticationResult;

    this.msalService.instance.setActiveAccount(payload.account);
    this.setToken(`${payload.tokenType} ${payload.accessToken}`);

    console.log('[AUTH] Token acquisito con successo');

    if (this.checkIfTokenAuthorized(payload.accessToken)) {
      this.store.dispatch(GetInspector());
    } else {
      console.warn('[AUTH] Token non autorizzato per il ruolo richiesto');
      this.router.navigateByUrl('403');
    }
  }


  // ---------------------------------------------------------------------------
  // Gestione errore MSAL con backoff esponenziale
  // ---------------------------------------------------------------------------

  private handleMsalError(result: EventMessage): void {
    this.authState.failureCount++;
    console.error(`[AUTH] Errore MSAL #${this.authState.failureCount}:`, result.error);

    // Per errori interaction_required (es. AADSTS160021 sessione scaduta),
    // non fare retry con backoff — serve un redirect interattivo immediato.
    if (result.error instanceof InteractionRequiredAuthError) {
      console.warn('[AUTH] Sessione scaduta o mancante, redirect per login interattivo');
      this.authState.failureCount = 0;
      const configs = msalGuardConfig.authRequest as any;
      this.msalService.loginRedirect(configs).subscribe({
        error: (err) => {
          console.error('[AUTH] Errore loginRedirect dopo interaction_required:', err);
        }
      });
      return;
    }

    if (this.authState.failureCount >= CB_FAILURE_THRESHOLD) {
      this.openCircuitBreaker();
      return;
    }

    if (this.authState.failureCount >= MAX_RETRIES) {
      console.warn('[AUTH] Max retry raggiunti, si aspetta il circuit breaker threshold');
      return;
    }

    // Backoff esponenziale: 2s, 4s, 8s, …
    const delay = BASE_DELAY_MS * Math.pow(2, this.authState.failureCount - 1);
    const now = Date.now();

    if (now - this.authState.lastLoginAttempt < delay) {
      console.warn(`[AUTH] Troppo presto per il retry (backoff: ${delay}ms)`);
      return;
    }

    this.authState.lastLoginAttempt = now;
    this.authState.isLoggingIn = true;

    console.log(`[AUTH] Retry #${this.authState.failureCount} programmato tra ${delay}ms`);

    setTimeout(() => {
      const configs = msalGuardConfig.authRequest as any;
      this.msalService.loginRedirect(configs).subscribe({
        error: (err) => {
          console.error('[AUTH] Errore durante retry loginRedirect:', err);
          this.authState.isLoggingIn = false;
        }
      });
    }, delay);
  }


  // ---------------------------------------------------------------------------
  // Circuit breaker
  // ---------------------------------------------------------------------------

  private isCircuitBreakerOpen(): boolean {
    if (!this.circuitBreaker.isOpen) { return false; }

    const now = Date.now();
    if (now - this.circuitBreaker.openedAt > CB_TIMEOUT_MS) {
      this.circuitBreaker.isOpen = false;
      this.authState.failureCount = 0;
      console.log('[AUTH] Circuit breaker CHIUSO — autenticazione riabilitata');
      return false;
    }

    const remainingMin = Math.ceil((CB_TIMEOUT_MS - (now - this.circuitBreaker.openedAt)) / 60_000);
    console.warn(`[AUTH] Circuit breaker APERTO — riprova tra circa ${remainingMin} minuto/i`);
    return true;
  }

  private openCircuitBreaker(): void {
    this.circuitBreaker.isOpen = true;
    this.circuitBreaker.openedAt = Date.now();
    console.error(`[AUTH] Circuit breaker APERTO dopo ${this.authState.failureCount} errori consecutivi`);
    this.errorManager.showErrorDialog(
      'Il sistema di autenticazione è temporaneamente non disponibile. Riprova tra qualche minuto.'
    );
  }


  // ---------------------------------------------------------------------------
  // Logout
  // ---------------------------------------------------------------------------

  async logout(): Promise<void> {
    localStorage.removeItem(ACCESS_TOKEN_LOCAL_STORAGE_KEY);
    this.msalService.logout();
  }


  // ---------------------------------------------------------------------------
  // Token helpers
  // ---------------------------------------------------------------------------

  setToken(token: string): void {
    localStorage.setItem(ACCESS_TOKEN_LOCAL_STORAGE_KEY, token);
    this.token = token;
  }

  get isLoggedIn(): boolean {
    return !!this.token;
  }

  get currentAccessToken(): string {
    return this.token;
  }

  get isInternalUser(): boolean {
    return !!this.internal;
  }

  get username(): string {
    return this.name;
  }

  checkIfTokenAuthorized(token: string): boolean {
    try {
      const payloadBase64 = token.split('.')[1];
      const payloadJson = atob(payloadBase64);
      const payload = JSON.parse(payloadJson);

      const roles = payload.roles;
      if (Array.isArray(roles)) {
        return roles.includes(environment.role);
      }

      return false;
    } catch (error) {
      console.error('[AUTH] Errore nel parsing del token JWT:', error);
      return false;
    }
  }
}
